// Iteration 081 -- Match scoring page.
// 084c: SSE live updates + clock + flash + per-team timeline + responsive
// two-column layout that stacks on mobile to mirror the Android app.
import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import type { RugbyMatchResultPublic, RugbyScoreEventPublic, RugbyMatchResultTeamSummary, RugbyScoreResponse, TeamSide, RugbyScoreEventType } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MatchClock } from "@/components/match-clock";
import { MatchScoreboard } from "@/components/match-scoreboard";
import { OfflineScoreBar } from "@/components/offline/offline-score-bar";
import { Undo2, Lock, Unlock } from "lucide-react";

/** Points-scoring buttons: big grid inside each team panel. Colours match
 *  the timeline event badges so the recorder's palette is consistent. */
const SCORE_BUTTONS: Array<{
  type: Exclude<RugbyScoreEventType, "correction" | "yellow_card" | "red_card">;
  label: string;
  points: number;
  className: string;
}> = [
  { type: "try", label: "Try", points: 5, className: "bg-green-600 hover:bg-green-700 text-white" },
  { type: "conversion", label: "Conversion", points: 2, className: "bg-sky-500 hover:bg-sky-600 text-white" },
  { type: "penalty", label: "Penalty", points: 3, className: "bg-orange-500 hover:bg-orange-600 text-white" },
  { type: "drop_goal", label: "Drop goal", points: 3, className: "bg-violet-500 hover:bg-violet-600 text-white" },
];

/** Play-time-only match minute, matching the Android helper. */
function currentMatchMinute(result: RugbyMatchResultPublic): number {
  const startsMs = new Date(result.starts_at).getTime();
  const now = Date.now();
  if (now < startsMs) return 0;
  const h1Ends = startsMs + result.first_half_mins * 60_000;
  const brEnds = h1Ends + result.break_mins * 60_000;
  const h2Ends = brEnds + result.second_half_mins * 60_000;
  if (now < h1Ends) return Math.floor((now - startsMs) / 60_000);
  if (now < brEnds) return result.first_half_mins;
  if (now < h2Ends) return result.first_half_mins + Math.floor((now - brEnds) / 60_000);
  return result.first_half_mins + result.second_half_mins;
}

export default function RugbySevensMatchScorePage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<RugbyScoreResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const reload = useCallback(() => {
    if (!id) return;
    setLoading(true);
    api
      .getRugbyMatchScore(id)
      .then(setData)
      .catch((e: unknown) => setError(e instanceof api.ApiError ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const [flash, setFlash] = useState<{ type: string; side: string; points: number; at: number } | null>(null);
  useEffect(() => {
    if (!id || !data?.result.event_id) return;
    const stop = api.subscribeRugbySevensScoreStream(data.result.event_id, (update) => {
      if (update.match_id !== id) return;
      api.getRugbySevensMatchScore(id).then(setData).catch(() => {});
      if (update.last_event_type && update.last_team_side && update.last_points != null) {
        setFlash({
          type: update.last_event_type,
          side: update.last_team_side,
          points: update.last_points,
          at: Date.now(),
        });
      }
    });
    return stop;
  }, [id, data?.result.event_id]);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 4_000);
    return () => clearTimeout(t);
  }, [flash?.at]);

  async function record(
    side: TeamSide,
    eventType: Exclude<RugbyScoreEventType, "correction">,
  ) {
    if (!id || !data) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.recordRugbySevensScoreEvent(id, {
        team_side: side,
        event_type: eventType,
        minute: currentMatchMinute(data.result),
      });
      setData(res);
    } catch (e) {
      if (e instanceof api.QueuedOfflineError) return; // saved offline; bar shows it
      setError(e instanceof api.ApiError ? e.message : "Failed to record event");
    } finally {
      setSubmitting(false);
    }
  }

  async function undo(eventId: string) {
    if (!id) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.deleteRugbySevensScoreEvent(id, eventId);
      setData(res);
    } catch (e) {
      if (e instanceof api.QueuedOfflineError) return;
      setError(e instanceof api.ApiError ? e.message : "Failed to undo");
    } finally {
      setSubmitting(false);
    }
  }

  async function finalize() {
    if (!id) return;
    if (!confirm("Mark this match as final?")) return;
    setSubmitting(true);
    try {
      const res = await api.finalizeRugbySevensMatchScore(id);
      setData(res);
    } catch (e) {
      if (e instanceof api.QueuedOfflineError) return;
      setError(e instanceof api.ApiError ? e.message : "Failed to finalize");
    } finally {
      setSubmitting(false);
    }
  }

  async function reopen() {
    if (!id) return;
    setSubmitting(true);
    try {
      const res = await api.reopenRugbySevensMatchScore(id);
      setData(res);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to reopen");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <p data-page="rugby-sevens-match-score" data-state="loading">Loading…</p>;
  if (!data) return <p data-page="rugby-sevens-match-score" data-state="empty">No score data</p>;

  const { result, events } = data;
  const canRecord = result._actions.can_record;
  const canFinalize = result._actions.can_finalize;
  const canCorrect = result._actions.can_correct;

  const startsMs = new Date(result.starts_at).getTime();
  const scheduledEndMs = startsMs + (result.first_half_mins + result.break_mins + result.second_half_mins) * 60_000;
  const now = Date.now();
  const hasStarted = now >= startsMs;
  const pastEnd = now > scheduledEndMs + 5 * 60_000;
  const isLive = !result.final && hasStarted && !pastEnd;
  const awaitingFinalise = !result.final && pastEnd;

  const homeEvents = events.filter((e) => e.team_side === "home");
  const awayEvents = events.filter((e) => e.team_side === "away");

  return (
    <div className="flex flex-col gap-4" data-page="rugby-sevens-match-score">
      {/* Header: breadcrumb + title + status */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <Link to={`/matches/${id}`} className="text-sm text-muted-foreground hover:underline">
            ← Back to match
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold">
              {result.age_group ? `${result.age_group} · Scoring` : "Scoring"}
            </h1>
            {result.final ? (
              <span className="inline-flex items-center rounded bg-muted px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground" data-status="final">
                Final
              </span>
            ) : isLive ? (
              <span className="inline-flex items-center gap-1 rounded bg-red-500 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-white" data-status="live">
                <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-white" aria-hidden />
                Live
              </span>
            ) : awaitingFinalise ? (
              <span className="inline-flex items-center rounded bg-muted px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground" data-status="full-time">
                Full time
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <MatchScoreboard
            home={result.home_team}
            away={result.away_team}
            homeScore={result.home_score}
            awayScore={result.away_score}
            flashSide={flash?.side === "home" ? "home" : flash?.side === "away" ? "away" : null}
            flashEventType={flash?.type ?? null}
          />
          {result.final && canCorrect && (
            <Button variant="outline" size="sm" onClick={reopen} disabled={submitting}>
              <Unlock className="mr-2 h-4 w-4" />
              Reopen
            </Button>
          )}
          {!result.final && canFinalize && (
            <Button variant="outline" size="sm" onClick={finalize} disabled={submitting}>
              <Lock className="mr-2 h-4 w-4" />
              Finalise
            </Button>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-destructive" data-error>{error}</p>}
      <OfflineScoreBar matchId={id} />
      {result.final && (
        <div className="rounded border bg-muted px-3 py-2 text-sm">Match finalised -- scores locked.</div>
      )}

      {/* Clock + referee line */}
      <MatchClock
        startsAt={result.starts_at}
        firstHalfMins={result.first_half_mins}
        breakMins={result.break_mins}
        secondHalfMins={result.second_half_mins}
        final={result.final}
      />
      {result.referee && (
        <p className="text-sm text-muted-foreground" data-field="referee">
          Ref · <span className="font-medium text-foreground">{result.referee}</span>
        </p>
      )}

      {/* Two team panels -- side-by-side on md+, stacked on mobile */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <TeamPanel
          side="home"
          team={result.home_team}
          events={homeEvents}
          flash={flash && flash.side === "home" ? flash : null}
          canRecord={canRecord}
          submitting={submitting}
          onRecord={(type) => record("home", type)}
          onUndo={undo}
        />
        <TeamPanel
          side="away"
          team={result.away_team}
          events={awayEvents}
          flash={flash && flash.side === "away" ? flash : null}
          canRecord={canRecord}
          submitting={submitting}
          onRecord={(type) => record("away", type)}
          onUndo={undo}
        />
      </div>
    </div>
  );
}

// ─── Team panel ─────────────────────────────────────────────────────

interface TeamPanelProps {
  side: TeamSide;
  team: RugbyMatchResultTeamSummary | null;
  events: RugbyScoreEventPublic[];
  flash: { type: string; side: string; points: number; at: number } | null;
  canRecord: boolean;
  submitting: boolean;
  onRecord: (eventType: Exclude<RugbyScoreEventType, "correction">) => void;
  onUndo: (eventId: string) => void;
}

function TeamPanel({ side, team, events, flash, canRecord, submitting, onRecord, onUndo }: TeamPanelProps) {
  return (
    <Card data-side={side} className="relative flex flex-col">
      {flash && (
        <div
          className={`absolute -top-3 left-1/2 z-10 -translate-x-1/2 animate-pulse rounded-full px-3 py-1 text-xs font-bold uppercase shadow ${flashClass(flash.type)}`}
          data-field="score-flash"
        >
          {flash.type.replace("_", " ")} {flash.points > 0 ? `+${flash.points}` : ""}
        </div>
      )}
      <CardContent className="flex flex-col gap-4 pt-5">
        {/* Team header */}
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-sm font-semibold uppercase tracking-wide text-muted-foreground" data-field={`team-name-${side}`}>
            {team?.team_display_name ?? (side === "home" ? "Home" : "Away")}
          </span>
          {team?.school_abbreviation && (
            <span className="text-xs text-muted-foreground">{team.school_abbreviation}</span>
          )}
        </div>

        {/* Scoring + discipline buttons -- only shown to recorders. Spectators
         *  see the timeline + scoreboard only. */}
        {canRecord && (
          <>
            <div className="grid grid-cols-2 gap-2">
              {SCORE_BUTTONS.map((b) => (
                <button
                  key={b.type}
                  type="button"
                  disabled={submitting}
                  onClick={() => onRecord(b.type)}
                  data-action={`score-${side}-${b.type}`}
                  className={`inline-flex h-11 items-center justify-center gap-1.5 rounded-md px-3 text-sm font-semibold shadow-sm disabled:opacity-50 ${b.className}`}
                >
                  {b.label}
                  <span className="text-xs opacity-80">+{b.points}</span>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                disabled={submitting}
                onClick={() => onRecord("yellow_card")}
                data-action={`score-${side}-yellow_card`}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-amber-400 px-3 py-1.5 text-xs font-bold uppercase text-amber-900 shadow-sm hover:bg-amber-500 disabled:opacity-50"
              >
                <CardGlyph color="amber" />
                Yellow
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={() => onRecord("red_card")}
                data-action={`score-${side}-red_card`}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-red-600 px-3 py-1.5 text-xs font-bold uppercase text-white shadow-sm hover:bg-red-700 disabled:opacity-50"
              >
                <CardGlyph color="red" />
                Red
              </button>
            </div>
          </>
        )}

        {/* Per-team timeline -- content-sized when empty/short, grows as
         *  events land, then caps at 50vh and scrolls internally. `transition`
         *  softens the growth; browsers can't animate auto-height so this is
         *  mostly useful when the cap kicks in. */}
        <div className="min-h-0 max-h-[50vh] overflow-y-auto transition-[max-height] duration-300 ease-out">
          <TeamTimeline events={events} canUndo={canRecord} submitting={submitting} onUndo={onUndo} />
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Per-team timeline ──────────────────────────────────────────────

function TeamTimeline({
  events,
  canUndo,
  submitting,
  onUndo,
}: {
  events: RugbyScoreEventPublic[];
  canUndo: boolean;
  submitting: boolean;
  onUndo: (eventId: string) => void;
}) {
  if (events.length === 0) {
    return <p className="text-xs text-muted-foreground">No events yet.</p>;
  }
  return (
    <ul className="space-y-1 text-sm" data-field="team-timeline">
      {events.slice().reverse().map((e) => (
        <li
          key={e.id}
          className="flex items-center gap-2 rounded border border-transparent bg-muted/30 px-2 py-1 hover:border-border"
        >
          <span className="w-10 font-mono text-xs text-muted-foreground">
            {e.minute != null ? `${e.minute}'` : "--"}
          </span>
          <EventBadge eventType={e.event_type} />
          <span className="flex-1 capitalize truncate">
            {e.event_type.replace("_", " ")}
          </span>
          {e.points !== 0 && (
            <span className="font-bold tabular-nums">
              {e.points > 0 ? `+${e.points}` : e.points}
            </span>
          )}
          {canUndo && (
            <button
              type="button"
              onClick={() => onUndo(e.id)}
              disabled={submitting}
              title="Undo this event"
              className="rounded p-1 text-muted-foreground hover:bg-background hover:text-foreground disabled:opacity-40"
              data-action={`undo-${e.id}`}
            >
              <Undo2 className="h-3.5 w-3.5" />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

// ─── Event type glyph ───────────────────────────────────────────────
//  Small colour-coded badge mirroring the Android palette.

function EventBadge({ eventType }: { eventType: RugbyScoreEventPublic["event_type"] }) {
  if (eventType === "yellow_card") return <CardGlyph color="amber" />;
  if (eventType === "red_card") return <CardGlyph color="red" />;

  const map: Record<string, { bg: string; letter: string }> = {
    try: { bg: "bg-green-600", letter: "T" },
    conversion: { bg: "bg-sky-500", letter: "C" },
    penalty: { bg: "bg-orange-500", letter: "P" },
    drop_goal: { bg: "bg-violet-500", letter: "D" },
    correction: { bg: "bg-muted-foreground", letter: "≈" },
  };
  const { bg, letter } = map[eventType] ?? { bg: "bg-muted-foreground", letter: "·" };
  return (
    <span
      className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${bg}`}
      aria-hidden
    >
      {letter}
    </span>
  );
}

function CardGlyph({ color }: { color: "amber" | "red" }) {
  const bg = color === "amber" ? "bg-amber-400" : "bg-red-600";
  return <span className={`inline-block h-4 w-3 shrink-0 rounded-sm ${bg}`} aria-hidden />;
}

/** Flash chip colour keyed by event type -- matches the timeline badge
 *  and the scoring button so every surface for a given event speaks in
 *  the same palette. */
function flashClass(eventType: string): string {
  switch (eventType) {
    case "try": return "bg-green-600 text-white";
    case "conversion": return "bg-sky-500 text-white";
    case "penalty": return "bg-orange-500 text-white";
    case "drop_goal": return "bg-violet-500 text-white";
    case "yellow_card": return "bg-amber-400 text-amber-900";
    case "red_card": return "bg-red-600 text-white";
    default: return "bg-amber-400 text-amber-900";
  }
}
